import {
  priorityOf,
  type Answers,
  type BuildingDto,
  type IssueDetail,
  type IssueEventDto,
  type IssueStatus,
  type IssueSummary,
} from "@klantpunt/shared";

/**
 * Een wijziging in de offline-wachtrij. Wordt in volgorde verstuurd; `id` is de Idempotency-Key,
 * zodat opnieuw versturen na een weggevallen verbinding niets dubbel doet.
 */
export interface Op {
  id: string;
  /** Volgorde van aanmaken. */
  seq: number;
  /** Alleen versturen met de sessie van dezelfde gebruiker in dezelfde organisatie. */
  userId: string;
  orgId: string;
  buildingId: string | null;
  /** Korte omschrijving voor meldingen ("Opmerking bij W455-0007"). */
  label: string;
  createdAt: string;
  req:
    | { kind: "http"; method: "POST" | "PUT" | "PATCH" | "DELETE"; path: string; body?: unknown }
    | { kind: "upload"; fileId: string; mime: string; name: string; size: number };
  /** Hoe de wijziging alvast zichtbaar is in de app, tot de server haar heeft verwerkt. */
  overlay?: Overlay;
}

export type Overlay =
  | { t: "create"; issue: IssueDetail }
  | { t: "status"; issueId: string; status: IssueStatus }
  | { t: "comment"; issueId: string; event: IssueEventDto }
  | { t: "photo"; issueId: string; fileId: string; mime?: string };

/** Markering op bevindingen die alleen nog op dit apparaat bestaan. */
export const isLocal = (i: IssueSummary) => Boolean((i as { local?: boolean }).local);

export function toSummary(d: IssueDetail): IssueSummary {
  const { events: _e, photos: _p, createdBy: _c, ...summary } = d;
  return summary;
}

/** Nieuwe bevinding zoals de server haar zou teruggeven, met code "W455-…" tot het nummer bekend is. */
export function localIssue(
  b: Pick<BuildingDto, "code">,
  input: {
    id: string;
    title: string;
    description: string | null;
    answers: Answers;
    installationId: string | null;
    elementCode?: string | null;
    locations: { floorId: string; roomId: string | null; x: number | null; y: number | null }[];
    photos: string[];
  },
  me: { id: string; name: string },
  now = new Date().toISOString(),
): IssueDetail {
  const issue: IssueDetail & { local: true } = {
    id: input.id,
    code: `${b.code}-…`,
    taskCode: null,
    seq: 0,
    title: input.title,
    description: input.description || null,
    status: "open",
    priority: priorityOf(input.answers),
    answers: input.answers,
    installationId: input.installationId,
    elementCode: input.elementCode ?? null,
    taskId: null,
    locations: input.locations.map((l, k) => ({ ...l, id: `${input.id}-${k}`, isPrimary: k === 0, sort: k })),
    photoCount: input.photos.length,
    commentCount: 0,
    createdAt: now,
    updatedAt: now,
    createdBy: me,
    photos: input.photos.map((fileId) => ({ fileId })),
    events: [
      {
        id: `${input.id}-created`,
        kind: "created",
        body: "Vastgelegd op dit apparaat · wacht op verbinding",
        user: me,
        createdAt: now,
      },
    ],
    local: true,
  };
  return issue;
}

function applyOne(d: IssueDetail, o: Overlay): IssueDetail {
  switch (o.t) {
    case "status":
      return { ...d, status: o.status };
    case "comment":
      return { ...d, events: [...d.events, o.event], commentCount: d.commentCount + 1 };
    case "photo":
      return d.photos.some((p) => p.fileId === o.fileId)
        ? d
        : { ...d, photos: [...d.photos, { fileId: o.fileId, mime: o.mime }], photoCount: d.photoCount + 1 };
    case "create":
      return d;
  }
}

const overlaysFor = (ops: readonly Op[], issueId: string) =>
  ops.flatMap((op) => (op.overlay && op.overlay.t !== "create" && op.overlay.issueId === issueId ? [op.overlay] : []));

/** Detail van de server (of niets, als die er nog niet is) plus de wijzigingen die nog wachten. */
export function overlayDetail(
  d: IssueDetail | undefined,
  ops: readonly Op[],
  issueId: string,
): IssueDetail | undefined {
  let out = d;
  if (!out) {
    const create = ops.find((op) => op.overlay?.t === "create" && op.overlay.issue.id === issueId)?.overlay;
    if (create?.t === "create") out = create.issue;
  }
  if (!out) return undefined;
  return overlaysFor(ops, issueId).reduce(applyOne, out);
}

/** Lijst van de server plus nieuwe bevindingen en wijzigingen die nog wachten. */
export function overlayList(list: IssueSummary[], ops: readonly Op[], buildingId: string): IssueSummary[] {
  const ids = new Set(list.map((i) => i.id));
  const out = list.map((i) => {
    const os = overlaysFor(ops, i.id);
    if (!os.length) return i;
    // Foto's en opmerkingen tellen bovenop wat de server al had.
    return toSummary(os.reduce(applyOne, { ...i, events: [], photos: [], createdBy: null } as IssueDetail));
  });
  for (const op of ops) {
    if (op.buildingId !== buildingId || op.overlay?.t !== "create" || ids.has(op.overlay.issue.id)) continue;
    const d = overlayDetail(op.overlay.issue, ops, op.overlay.issue.id)!;
    out.push(toSummary(d));
  }
  return out;
}

/** Eén nieuwe wijziging direct toepassen op wat al in de cache staat. */
export function applyOverlay(d: IssueDetail, o: Overlay): IssueDetail {
  return applyOne(d, o);
}
